import React, { forwardRef, useImperativeHandle } from 'react';

import COLORS from '../../styles/emotion/colors3';
import styles from './confettiV2.module.less';

const DEFAULT_CONFETTI_COLORS = [
  COLORS.BAY_100,
  COLORS.CLOUD_100,
  COLORS.COVE_100,
  COLORS.MARINE_100,
  COLORS.SKY_100,
];

export type ConfettiV2Handle = {
  triggerConfettiAnimation: () => void;
};

export type ConfettiV2Props = {
  /** Any number of confetti colors; defaults to Zola brand colors */
  confettiColors?: string[];
  /** Optional number that determines vertical spread of confetti; defaults to 200 */
  confettiSpreadHeight?: number;
  /** Optional number that determines horizontal spread of confetti; defaults to 300 */
  confettiSpreadWidth?: number;
  /** Ref to container wrapping target and ConfettiV2; container should be relatively positioned */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Ref from which confetti will fly out */
  targetRef: React.RefObject<HTMLElement | null>;
};

/**
 * Confetti animation component that depends on three refs:
 *   - **containerRef -** ref to container wrapping target and ConfettiV2; container should be relatively positioned
 *   - **targetRef -** target ref from which confetti will fly out; can be a button or any other element
 *   - **ref -** ref to ConfettiV2 component itself
 *
 * Should be loaded like the following:
 * ```
 * <div ref={containerRef} style={{ position: 'relative' }}>
 *   <ButtonV3
 *     onClick={() => confettiRef.current?.triggerConfettiAnimation()}
 *     innerRef={targetRef}
 *   >
 *     Bring forth the confetti!
 *   </ButtonV3>
 *   <ConfettiV2 containerRef={containerRef} ref={confettiRef} targetRef={targetRef} />
 * </div>
 * ```
 *
 * Trigger for the confetti doesn't have to be on click of the target.
 * The target ref just determines where the confetti originates from.
 * For instance, the trigger could be timed, independent of user interaction:
 * ```
 * setTimeout(() => {
 *   confettiRef.current?.triggerConfettiAnimation();
 * }, 5000);
 * ```
 */
export const ConfettiV2 = forwardRef<ConfettiV2Handle, ConfettiV2Props>(
  (
    {
      confettiColors = DEFAULT_CONFETTI_COLORS,
      confettiSpreadHeight = 200,
      confettiSpreadWidth = 300,
      containerRef,
      targetRef,
    }: ConfettiV2Props,
    ref
  ) => {
    const triggerConfettiAnimation = () => {
      for (let i = 0; i < 40; i += 1) {
        const confetti = document.createElement('div');
        confetti.className = styles.confetti;

        // Set random color per confetti
        confetti.style.setProperty(
          '--confetti-color',
          confettiColors[Math.floor(Math.random() * confettiColors.length)]
        );

        // Set random position and movement per confetti
        const dx = (Math.random() - 0.5) * confettiSpreadWidth; // Horizontal spread
        const dy = (Math.random() - 0.5) * confettiSpreadHeight; // Vertical spread
        const rotate = `${Math.random() * 360}deg`; // Rotation
        confetti.style.setProperty('--dx', `${dx}px`);
        confetti.style.setProperty('--dy', `${dy}px`);
        confetti.style.setProperty('--rotate', rotate);

        // Set confetti position relative to the target
        const rect = targetRef.current?.getBoundingClientRect();
        if (rect) {
          confetti.style.left = `${rect.width / 2}px`;
          confetti.style.top = `${rect.height / 2}px`;
        }

        // Append confetti to container
        containerRef.current?.appendChild(confetti);

        // Remove confetti after animation
        confetti.addEventListener('animationend', () => {
          confetti.remove();
        });
      }
    };

    // Expose the triggerConfettiAnimation method via the ref
    useImperativeHandle(ref, () => ({
      triggerConfettiAnimation,
    }));

    return <></>;
  }
);
